/**
 * Interface languages. These are the languages DubForge's own screens and Discord notifications are
 * written in (separate from LANGUAGES, the languages work can be dubbed into).
 */
export const UI_LOCALES = [
  { code: 'en', name: 'English', dir: 'ltr', translate: 'en' },
  { code: 'es-419', name: 'Español (Latinoamérica)', dir: 'ltr', translate: 'es-MX' },
  { code: 'es-ES', name: 'Español (España)', dir: 'ltr', translate: 'es' },
  { code: 'pt-BR', name: 'Português (Brasil)', dir: 'ltr', translate: 'pt' },
  { code: 'fr', name: 'Français', dir: 'ltr', translate: 'fr' },
  { code: 'de', name: 'Deutsch', dir: 'ltr', translate: 'de' },
  { code: 'it', name: 'Italiano', dir: 'ltr', translate: 'it' },
  { code: 'ja', name: '日本語', dir: 'ltr', translate: 'ja' },
  { code: 'ko', name: '한국어', dir: 'ltr', translate: 'ko' },
  { code: 'zh-Hans', name: '简体中文', dir: 'ltr', translate: 'zh' },
  { code: 'ru', name: 'Русский', dir: 'ltr', translate: 'ru' },
  { code: 'hi', name: 'हिन्दी', dir: 'ltr', translate: 'hi' },
  { code: 'id', name: 'Bahasa Indonesia', dir: 'ltr', translate: 'id' },
  { code: 'tr', name: 'Türkçe', dir: 'ltr', translate: 'tr' },
  { code: 'ar', name: 'العربية', dir: 'rtl', translate: 'ar' },
] as const;

export type UiLocale = (typeof UI_LOCALES)[number]['code'];
export const UI_LOCALE_CODES = UI_LOCALES.map((l) => l.code) as [UiLocale, ...UiLocale[]];
export const DEFAULT_UI_LOCALE: UiLocale = 'en';

export function isUiLocale(value: unknown): value is UiLocale {
  return typeof value === 'string' && (UI_LOCALE_CODES as string[]).includes(value);
}

export function uiLocaleInfo(code: UiLocale) {
  return UI_LOCALES.find((l) => l.code === code)!;
}

/** Amazon Translate language code for an interface language. */
export function translateLanguageCode(code: UiLocale): string {
  return uiLocaleInfo(code).translate;
}

/** Picks the closest interface language for a list of browser language tags (navigator.languages). */
export function matchUiLocale(tags: readonly string[]): UiLocale {
  for (const raw of tags) {
    const tag = raw.trim();
    if (isUiLocale(tag)) return tag;
    const lower = tag.toLowerCase();
    const [base, ...rest] = lower.split('-');
    if (base === 'es') return rest.includes('es') ? 'es-ES' : 'es-419';
    if (base === 'pt') return 'pt-BR';
    if (base === 'zh') {
      // Traditional Chinese readers aren't served well by Simplified; keep looking.
      if (rest.some((p) => ['hant', 'tw', 'hk', 'mo'].includes(p))) continue;
      return 'zh-Hans';
    }
    const match = UI_LOCALE_CODES.find((c) => c === base);
    if (match) return match;
  }
  return DEFAULT_UI_LOCALE;
}

/** Discord notification texts. {language} is a dub language name, already in the reader's language. */
export interface NotificationTexts {
  assignment_created: string;
  submission_created: string;
  changes_requested: string;
  approved: string;
  test: string;
  open: string;
  /** Email subject for changes_requested (its message includes the note, too long for a subject). */
  changes_requested_subject: string;
  /** Email footer explaining why the reader gets these emails. */
  footer: string;
}

export const NOTIFICATION_TEXTS: Record<UiLocale, NotificationTexts> = {
  en: {
    assignment_created: 'New dub assignment: "{project}" ({language}).',
    submission_created: '{contractor} uploaded version {version} of the {language} dub for "{project}".',
    changes_requested: 'Changes requested on your {language} dub for "{project}": {note}',
    approved: 'Your {language} dub for "{project}" was approved. Thank you!',
    test: 'This is a test message from DubForge. If you can read this, notifications are working.',
    open: 'Open in DubForge',
    changes_requested_subject: 'Changes requested: "{project}" ({language})',
    footer:
      'You get these emails because you work on dubs for this channel. To use a different address, open DubForge and choose Notification email in the account menu.',
  },
  'es-419': {
    assignment_created: 'Nueva asignación de doblaje: "{project}" ({language}).',
    submission_created: '{contractor} subió la versión {version} del doblaje en {language} de "{project}".',
    changes_requested: 'Se solicitaron cambios en tu doblaje en {language} de "{project}": {note}',
    approved: 'Tu doblaje en {language} de "{project}" fue aprobado. ¡Gracias!',
    test: 'Este es un mensaje de prueba de DubForge. Si puedes leerlo, las notificaciones funcionan.',
    open: 'Abrir en DubForge',
    changes_requested_subject: 'Cambios solicitados: "{project}" ({language})',
    footer:
      'Recibes estos correos porque trabajas en doblajes para este canal. Para usar otra dirección, abre DubForge y elige Correo para notificaciones en el menú de tu cuenta.',
  },
  'es-ES': {
    assignment_created: 'Nueva asignación de doblaje: «{project}» ({language}).',
    submission_created: '{contractor} ha subido la versión {version} del doblaje en {language} de «{project}».',
    changes_requested: 'Se han solicitado cambios en tu doblaje en {language} de «{project}»: {note}',
    approved: 'Tu doblaje en {language} de «{project}» ha sido aprobado. ¡Gracias!',
    test: 'Este es un mensaje de prueba de DubForge. Si puedes leerlo, las notificaciones funcionan.',
    open: 'Abrir en DubForge',
    changes_requested_subject: 'Cambios solicitados: «{project}» ({language})',
    footer:
      'Recibes estos correos porque trabajas en doblajes para este canal. Para usar otra dirección, abre DubForge y elige Correo para notificaciones en el menú de tu cuenta.',
  },
  'pt-BR': {
    assignment_created: 'Nova tarefa de dublagem: "{project}" ({language}).',
    submission_created: '{contractor} enviou a versão {version} da dublagem em {language} de "{project}".',
    changes_requested: 'Foram solicitadas alterações na sua dublagem em {language} de "{project}": {note}',
    approved: 'Sua dublagem em {language} de "{project}" foi aprovada. Obrigado!',
    test: 'Esta é uma mensagem de teste do DubForge. Se você consegue ler isto, as notificações estão funcionando.',
    open: 'Abrir no DubForge',
    changes_requested_subject: 'Alterações solicitadas: "{project}" ({language})',
    footer:
      'Você recebe estes e-mails porque trabalha em dublagens para este canal. Para usar outro endereço, abra o DubForge e escolha E-mail para notificações no menu da sua conta.',
  },
  fr: {
    assignment_created: 'Nouvelle mission de doublage : « {project} » ({language}).',
    submission_created: '{contractor} a envoyé la version {version} du doublage en {language} de « {project} ».',
    changes_requested: 'Des modifications ont été demandées pour votre doublage en {language} de « {project} » : {note}',
    approved: 'Votre doublage en {language} de « {project} » a été approuvé. Merci !',
    test: 'Ceci est un message de test de DubForge. Si vous pouvez le lire, les notifications fonctionnent.',
    open: 'Ouvrir dans DubForge',
    changes_requested_subject: 'Modifications demandées : « {project} » ({language})',
    footer:
      'Vous recevez ces e-mails parce que vous travaillez sur des doublages pour cette chaîne. Pour utiliser une autre adresse, ouvrez DubForge et choisissez E-mail de notification dans le menu de votre compte.',
  },
  de: {
    assignment_created: 'Neuer Synchronauftrag: „{project}“ ({language}).',
    submission_created: '{contractor} hat Version {version} der Synchronisation auf {language} für „{project}“ hochgeladen.',
    changes_requested: 'Für deine Synchronisation auf {language} von „{project}“ wurden Änderungen angefordert: {note}',
    approved: 'Deine Synchronisation auf {language} von „{project}“ wurde freigegeben. Danke!',
    test: 'Dies ist eine Testnachricht von DubForge. Wenn du sie lesen kannst, funktionieren die Benachrichtigungen.',
    open: 'In DubForge öffnen',
    changes_requested_subject: 'Änderungen angefordert: „{project}“ ({language})',
    footer:
      'Du bekommst diese E-Mails, weil du Synchronisationen für diesen Kanal machst. Um eine andere Adresse zu verwenden, öffne DubForge und wähle im Kontomenü „E-Mail für Benachrichtigungen“.',
  },
  it: {
    assignment_created: 'Nuovo incarico di doppiaggio: "{project}" ({language}).',
    submission_created: '{contractor} ha caricato la versione {version} del doppiaggio in {language} di "{project}".',
    changes_requested: 'Sono state richieste modifiche al tuo doppiaggio in {language} di "{project}": {note}',
    approved: 'Il tuo doppiaggio in {language} di "{project}" è stato approvato. Grazie!',
    test: 'Questo è un messaggio di prova di DubForge. Se riesci a leggerlo, le notifiche funzionano.',
    open: 'Apri in DubForge',
    changes_requested_subject: 'Modifiche richieste: "{project}" ({language})',
    footer:
      'Ricevi queste email perché lavori ai doppiaggi di questo canale. Per usare un altro indirizzo, apri DubForge e scegli Email per le notifiche nel menu del tuo account.',
  },
  ja: {
    assignment_created: '新しい吹き替えの依頼：「{project}」（{language}）',
    submission_created: '{contractor}さんが「{project}」の吹き替え（{language}、バージョン{version}）をアップロードしました。',
    changes_requested: '「{project}」の吹き替え（{language}）に修正依頼があります：{note}',
    approved: '「{project}」の吹き替え（{language}）が承認されました。ありがとうございました！',
    test: 'これはDubForgeからのテストメッセージです。このメッセージが読めれば、通知は正常に機能しています。',
    open: 'DubForgeで開く',
    changes_requested_subject: '修正依頼：「{project}」（{language}）',
    footer:
      'このチャンネルの吹き替えを担当しているため、このメールをお送りしています。別のアドレスを使うには、DubForgeを開き、アカウントメニューの「通知用メールアドレス」を選んでください。',
  },
  ko: {
    assignment_created: '새 더빙 작업: "{project}" ({language})',
    submission_created: '{contractor} 님이 "{project}"의 {language} 더빙 버전 {version}을(를) 업로드했습니다.',
    changes_requested: '"{project}"의 {language} 더빙에 수정 요청이 있습니다: {note}',
    approved: '"{project}"의 {language} 더빙이 승인되었습니다. 감사합니다!',
    test: 'DubForge에서 보낸 테스트 메시지입니다. 이 메시지가 보이면 알림이 정상적으로 작동하는 것입니다.',
    open: 'DubForge에서 열기',
    changes_requested_subject: '수정 요청: "{project}" ({language})',
    footer:
      '이 채널의 더빙 작업을 하고 계셔서 이 이메일을 보내 드립니다. 다른 주소를 사용하려면 DubForge를 열고 계정 메뉴에서 알림 이메일을 선택하세요.',
  },
  'zh-Hans': {
    assignment_created: '新的配音任务：“{project}”（{language}）',
    submission_created: '{contractor} 上传了“{project}”的{language}配音第 {version} 版。',
    changes_requested: '你为“{project}”录制的{language}配音需要修改：{note}',
    approved: '你为“{project}”录制的{language}配音已通过审核。谢谢！',
    test: '这是一条来自 DubForge 的测试消息。如果你能看到它，说明通知功能正常。',
    open: '在 DubForge 中打开',
    changes_requested_subject: '需要修改：“{project}”（{language}）',
    footer:
      '你收到这些邮件是因为你在为本频道配音。如需使用其他地址，请打开 DubForge，在账户菜单中选择“通知邮箱”。',
  },
  ru: {
    assignment_created: 'Новое задание на дубляж: «{project}» ({language}).',
    submission_created: '{contractor} загрузил(а) версию {version} дубляжа «{project}» ({language}).',
    changes_requested: 'По вашему дубляжу «{project}» ({language}) запрошены правки: {note}',
    approved: 'Ваш дубляж «{project}» ({language}) одобрен. Спасибо!',
    test: 'Это тестовое сообщение от DubForge. Если вы его видите, уведомления работают.',
    open: 'Открыть в DubForge',
    changes_requested_subject: 'Нужны правки: «{project}» ({language})',
    footer:
      'Вы получаете эти письма, потому что работаете над дубляжом для этого канала. Чтобы использовать другой адрес, откройте DubForge и выберите «Почта для уведомлений» в меню аккаунта.',
  },
  hi: {
    assignment_created: 'नया डबिंग असाइनमेंट: "{project}" ({language})।',
    submission_created: '{contractor} ने "{project}" की {language} डबिंग का संस्करण {version} अपलोड किया है।',
    changes_requested: '"{project}" की आपकी {language} डबिंग में बदलाव का अनुरोध किया गया है: {note}',
    approved: '"{project}" की आपकी {language} डबिंग स्वीकृत हो गई है। धन्यवाद!',
    test: 'यह DubForge का एक परीक्षण संदेश है। अगर आप इसे पढ़ पा रहे हैं, तो सूचनाएँ ठीक से काम कर रही हैं।',
    open: 'DubForge में खोलें',
    changes_requested_subject: 'बदलाव का अनुरोध: "{project}" ({language})',
    footer:
      'आपको ये ईमेल इसलिए मिलते हैं क्योंकि आप इस चैनल के लिए डबिंग करते हैं। कोई दूसरा पता इस्तेमाल करने के लिए DubForge खोलें और खाता मेनू में "सूचनाओं के लिए ईमेल" चुनें।',
  },
  id: {
    assignment_created: 'Tugas sulih suara baru: "{project}" ({language}).',
    submission_created: '{contractor} mengunggah versi {version} sulih suara {language} untuk "{project}".',
    changes_requested: 'Ada permintaan revisi untuk sulih suara {language} Anda pada "{project}": {note}',
    approved: 'Sulih suara {language} Anda untuk "{project}" telah disetujui. Terima kasih!',
    test: 'Ini adalah pesan uji dari DubForge. Jika Anda dapat membacanya, notifikasi berfungsi dengan baik.',
    open: 'Buka di DubForge',
    changes_requested_subject: 'Perlu revisi: "{project}" ({language})',
    footer:
      'Anda menerima email ini karena Anda mengerjakan sulih suara untuk channel ini. Untuk memakai alamat lain, buka DubForge lalu pilih Email notifikasi di menu akun.',
  },
  tr: {
    assignment_created: 'Yeni seslendirme görevi: "{project}" ({language}).',
    submission_created: '{contractor}, "{project}" seslendirmesinin ({language}) {version}. sürümünü yükledi.',
    changes_requested: '"{project}" için yaptığınız seslendirmede ({language}) değişiklik istendi: {note}',
    approved: '"{project}" için yaptığınız seslendirme ({language}) onaylandı. Teşekkürler!',
    test: "Bu, DubForge'dan bir test mesajıdır. Bunu okuyabiliyorsanız bildirimler çalışıyor demektir.",
    open: "DubForge'da aç",
    changes_requested_subject: 'Değişiklik istendi: "{project}" ({language})',
    footer:
      "Bu kanal için seslendirme yaptığınız için bu e-postaları alıyorsunuz. Başka bir adres kullanmak için DubForge'u açın ve hesap menüsünden Bildirim e-postası'nı seçin.",
  },
  ar: {
    assignment_created: 'مهمة دبلجة جديدة: "{project}" ({language}).',
    submission_created: 'رفع {contractor} الإصدار {version} من الدبلجة ({language}) لمشروع "{project}".',
    changes_requested: 'طُلبت تعديلات على دبلجتك ({language}) لمشروع "{project}": {note}',
    approved: 'تمت الموافقة على دبلجتك ({language}) لمشروع "{project}". شكرًا لك!',
    test: 'هذه رسالة تجريبية من DubForge. إذا كنت تستطيع قراءتها، فالإشعارات تعمل بشكل صحيح.',
    open: 'افتح في DubForge',
    changes_requested_subject: 'مطلوب تعديلات: "{project}" ({language})',
    footer:
      'تصلك هذه الرسائل لأنك تعمل على الدبلجة لهذه القناة. لاستخدام عنوان آخر، افتح DubForge واختر "بريد الإشعارات" من قائمة الحساب.',
  },
};

export interface NotificationParams {
  project?: string;
  /** Dub language code, e.g. "es-419". Rendered as a name in the reader's language. */
  language?: string;
  contractor?: string;
  version?: number;
  note?: string;
}

export function fillTemplate(template: string, values: Record<string, string | number | undefined>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''));
}
